import {gql} from '@apollo/client';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {Tag} from 'folder-items/FileConstants';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import TagValueViewer from 'metadata/TagValueViewer';
import {processTaskDetailsURL} from 'processes/dao/ProcessManagementDAO';
import ProcessTaskFileState from 'processes/process-task-details/ProcessTaskFileState';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import SmartChip from 'ui-library/chips/SmartChip';
import useSmartChipQuery, {SmartChipType} from 'ui-library/chips/useSmartChipQuery';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

import {
  ProcessOutputChipPopoverQuery,
  ProcessOutputChipPopoverQueryVariables,
} from './__generated/ProcessOutputChipPopoverQuery';
import {ChipSize} from './Chip';
import ChipPopoverContent from './ChipPopoverContent';
import {generateKeyForSchemaField} from './ChipPopoverContentUtils';
import ProcessTaskChip from './ProcessTaskChip';
import QueryingChipPopoverContent from './QueryingChipPopoverContent';

type ProcessOutput = ProcessOutputChipPopoverQuery['processOutput'];

export interface ProcessOutputChipProps extends SharedSmartChipProps<SmartChipType.PROCESS_OUTPUT> {
  enableFancyChipPopoverContent?: boolean;
}

interface OutputChipPopoverContentsProps {
  output: ProcessOutput;
  icon: BenchlingIconName | null;
}

export const PROCESS_OUTPUT_CHIP_POPOVER_QUERY = gql`
  query ProcessOutputChipPopoverQuery($outputId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    processOutput(id: $outputId) {
      ... on ProcessOutputNode {
        id
        name
        schema {
          id
          name
        }
        task {
          id
        }
        fields
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function OutputChipPopoverContents(props: OutputChipPopoverContentsProps): JSX.Element {
  const output = props.output;

  if (isInaccessible(output)) {
    return <>(No access)</>;
  }

  const fields = [
    {
      label: 'Name',
      value: (
        <>
          {props.icon != null ? <BenchlingIcon icon={props.icon} /> : null}
          &nbsp; {output.name}
        </>
      ),
    },
    {
      label: 'Schema',
      value: <>{output.schema.name}</>,
    },
    {
      label: 'Task',
      value: <ProcessTaskChip id={output.task.id} size={ChipSize.MINI} />,
    },
    ...(output.fields as Array<Tag>).map((tag) => ({
      generateStableKey: () => generateKeyForSchemaField(tag),
      label: tag.name,
      value: (
        <TagValueViewer
          tag={tag}
          chipSize={ChipSize.MINI}
          shouldPreviewImages={true}
          showChipsAsLinks={true}
          summarize={true}
          summarizeThreshold={3}
        />
      ),
    })),
  ];

  return <ChipPopoverContent info={{fields}} />;
}

export default function ProcessOutputChip(props: ProcessOutputChipProps): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const {
    item: processOutput,
    handleClickLink,
    isInaccessible,
    itemUrl: processUrl,
  } = useSmartChipQuery<SmartChipType.PROCESS_OUTPUT>({
    id,
    onClick,
    getUrlFn: ({task, process}) => processTaskDetailsURL(task.id),
  });

  const icon: BenchlingIconName | null = processOutput != null ? 'process-output' : null;
  const enableFancyChipPopoverContent = props.enableFancyChipPopoverContent ?? true;

  const onClickHandler = (e: BasicEvent): void => {
    if (processOutput) {
      if (onClick != null) {
        handleClickLink(e);
      } else {
        FileBrowserActions.openFile(processOutput.task.id, {
          onOpen: (fileState: ProcessTaskFileState) => {
            fileState.activateOutputsTab();
          },
        });
      }
    }
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_TASK_OR_OUTPUT_CHIP);
  };

  return (
    <SmartChip
      {...sharedSmartChipProps}
      icon={processOutput != null ? 'process-output' : null}
      isInaccessible={isInaccessible}
      item={processOutput}
      onClick={onClickHandler}
      url={processUrl}
      popoverContentOverride={
        enableFancyChipPopoverContent && id != null ? (
          <QueryingProcessOutputChipPopoverContent id={id} icon={icon} />
        ) : null
      }
    />
  );
}

/** @package */
export function QueryingProcessOutputChipPopoverContent(props: {
  id: string;
  icon: BenchlingIconName | null;
}): JSX.Element {
  return (
    <QueryingChipPopoverContent<ProcessOutputChipPopoverQuery, ProcessOutputChipPopoverQueryVariables>
      variables={{outputId: props.id}}
      query={PROCESS_OUTPUT_CHIP_POPOVER_QUERY}
    >
      {(data: ProcessOutputChipPopoverQuery) => (
        <OutputChipPopoverContents output={data.processOutput} icon={props.icon} />
      )}
    </QueryingChipPopoverContent>
  );
}
